import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  PropsWithChildren,
  useEffect,
} from 'react';

interface LoadingContextType {
  isLoading: boolean;
  setLoading: (loading: boolean, source?: string) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within LoadingProvider');
  }
  return context;
};

export const LoadingProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingSources, setLoadingSources] = useState<Set<string>>(new Set());

  const setLoading = useCallback((loading: boolean, source = 'unknown') => {
    setLoadingSources((prev) => {
      const newSet = new Set(prev);
      if (loading) {
        newSet.add(source);
      } else {
        newSet.delete(source);
      }
      return newSet;
    });
  }, []);

  // Update isLoading based on active sources
  useEffect(() => {
    const shouldLoad = loadingSources.size > 0;
    if (isLoading !== shouldLoad) {
      setIsLoading(shouldLoad);
    }
  }, [loadingSources, isLoading]);

  useEffect(() => {
    // Start with loader showing, it will be hidden when components report ready
    setLoading(true, 'initial');
  }, [setLoading]);

  return (
    <LoadingContext.Provider value={{ isLoading, setLoading }}>
      {/* Loading overlay with backdrop - makes loader visible on blank screen */}
      {isLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999998,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'opacity 0.3s ease-in-out',
            pointerEvents: 'none',
          }}
        >
          {/* Animated loading spinner - no text */}
          <div
            style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #333333',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
        </div>
      )}

      {/* Slim linear progress bar at top - shows progress during navigation */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          zIndex: 999999,
          opacity: isLoading ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: 'none',
          visibility: isLoading ? 'visible' : 'hidden',
          overflow: 'hidden',
          background: 'rgba(0, 0, 0, 0.05)',
        }}
      >
        <div
          className="slim-loader-bar"
          style={{
            position: 'absolute',
            height: '100%',
            width: '100%',
            background:
              'linear-gradient(90deg, transparent 0%, #333333 40%, #333333 60%, transparent 100%)',
            animation: 'slimLoaderMove 1.5s ease-in-out infinite',
          }}
        />
      </div>

      {/* Add keyframes for animations */}
      <style>{`
        @keyframes slimLoaderMove {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>

      {children}
    </LoadingContext.Provider>
  );
};
